import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportSummaryTable = ({ selectedReport, dateRange, filters }) => {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Generate mock table data based on selected report
  const generateTableData = useMemo(() => {
    if (!selectedReport) return [];

    const { categoryId, reportId } = selectedReport;

    switch (categoryId) {
      case 'card-usage':
        return Array.from({ length: 50 }, (_, i) => ({
          id: `NFC-${String(i + 1).padStart(3, '0')}`,
          cardName: `Business Card ${i + 1}`,
          user: `User ${i + 1}`,
          status: ['active', 'inactive', 'restricted'][Math.floor(Math.random() * 3)],
          usageCount: Math.floor(Math.random() * 100) + 10,
          lastUsed: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
          successRate: Math.floor(Math.random() * 30) + 70,
          department: ['Sales', 'Marketing', 'IT', 'HR'][Math.floor(Math.random() * 4)]
        }));

      case 'payment-analytics':
        return Array.from({ length: 50 }, (_, i) => ({
          id: `PAY-${String(i + 1).padStart(3, '0')}`,
          amount: `$${(Math.random() * 500 + 50).toFixed(2)}`,
          method: ['Credit Card', 'Bank Transfer', 'Digital Wallet'][Math.floor(Math.random() * 3)],
          status: ['completed', 'pending', 'failed'][Math.floor(Math.random() * 3)],
          date: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
          user: `User ${i + 1}`,
          cardId: `NFC-${String(Math.floor(Math.random() * 100) + 1).padStart(3, '0')}`,
          fees: `$${(Math.random() * 10 + 1).toFixed(2)}`
        }));

      case 'user-activity':
        return Array.from({ length: 50 }, (_, i) => ({
          id: `USR-${String(i + 1).padStart(3, '0')}`,
          name: `User ${i + 1}`,
          email: `user${i + 1}@example.com`,
          type: ['individual', 'team'][Math.floor(Math.random() * 2)],
          lastLogin: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
          sessionDuration: `${Math.floor(Math.random() * 60) + 5} min`,
          cardsOwned: Math.floor(Math.random() * 5) + 1,
          status: ['active', 'inactive'][Math.floor(Math.random() * 2)]
        }));

      case 'billing-summaries':
        return Array.from({ length: 50 }, (_, i) => ({
          id: `INV-${String(i + 1).padStart(3, '0')}`,
          user: `User ${i + 1}`,
          amount: `$${(Math.random() * 1000 + 100).toFixed(2)}`,
          status: ['paid', 'pending', 'overdue'][Math.floor(Math.random() * 3)],
          dueDate: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
          paidDate: Math.random() > 0.5 ? new Date(Date.now() - Math.random() * 10 * 24 * 60 * 60 * 1000).toLocaleDateString() : '-',
          plan: ['Basic', 'Standard', 'Premium'][Math.floor(Math.random() * 3)],
          period: 'Monthly'
        }));

      default:
        return [];
    }
  }, [selectedReport]);

  // Get table columns based on report type
  const getTableColumns = () => {
    if (!selectedReport) return [];

    const { categoryId } = selectedReport;

    switch (categoryId) {
      case 'card-usage':
        return [
          { key: 'id', label: 'Card ID', sortable: true },
          { key: 'cardName', label: 'Card Name', sortable: true },
          { key: 'user', label: 'User', sortable: true },
          { key: 'status', label: 'Status', sortable: true },
          { key: 'usageCount', label: 'Usage Count', sortable: true },
          { key: 'lastUsed', label: 'Last Used', sortable: true },
          { key: 'successRate', label: 'Success Rate', sortable: true },
          { key: 'department', label: 'Department', sortable: true }
        ];

      case 'payment-analytics':
        return [
          { key: 'id', label: 'Payment ID', sortable: true },
          { key: 'amount', label: 'Amount', sortable: true },
          { key: 'method', label: 'Method', sortable: true },
          { key: 'status', label: 'Status', sortable: true },
          { key: 'date', label: 'Date', sortable: true },
          { key: 'user', label: 'User', sortable: true },
          { key: 'cardId', label: 'Card ID', sortable: true },
          { key: 'fees', label: 'Fees', sortable: true }
        ];

      case 'user-activity':
        return [
          { key: 'id', label: 'User ID', sortable: true },
          { key: 'name', label: 'Name', sortable: true },
          { key: 'email', label: 'Email', sortable: true },
          { key: 'type', label: 'Type', sortable: true },
          { key: 'lastLogin', label: 'Last Login', sortable: true },
          { key: 'sessionDuration', label: 'Session Duration', sortable: true },
          { key: 'cardsOwned', label: 'Cards Owned', sortable: true },
          { key: 'status', label: 'Status', sortable: true }
        ];

      case 'billing-summaries':
        return [
          { key: 'id', label: 'Invoice ID', sortable: true },
          { key: 'user', label: 'User', sortable: true },
          { key: 'amount', label: 'Amount', sortable: true },
          { key: 'status', label: 'Status', sortable: true },
          { key: 'dueDate', label: 'Due Date', sortable: true },
          { key: 'paidDate', label: 'Paid Date', sortable: true },
          { key: 'plan', label: 'Plan', sortable: true },
          { key: 'period', label: 'Period', sortable: true }
        ];

      default:
        return [];
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortField) return generateTableData;

    return [...generateTableData].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric values
      if (typeof aVal === 'string' && aVal.includes('$')) {
        aVal = parseFloat(aVal.replace('$', ''));
        bVal = parseFloat(bVal.replace('$', ''));
      }

      if (typeof aVal === 'string' && !isNaN(aVal)) {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [generateTableData, sortField, sortDirection]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-success/20 text-success',
      inactive: 'bg-text-muted/20 text-text-muted',
      restricted: 'bg-warning/20 text-warning',
      completed: 'bg-success/20 text-success',
      pending: 'bg-warning/20 text-warning',
      failed: 'bg-error/20 text-error',
      paid: 'bg-success/20 text-success',
      overdue: 'bg-error/20 text-error'
    };
    return colors[status] || 'bg-text-muted/20 text-text-muted';
  };

  const renderCellContent = (item, column) => {
    const value = item[column.key];

    if (column.key === 'status') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(value)}`}>
          {value}
        </span>
      );
    }

    if (column.key === 'successRate') {
      return `${value}%`;
    }

    return value;
  };

  if (!selectedReport) {
    return (
      <div className="flex-1 flex items-center justify-center bg-background">
        <div className="text-center">
          <Icon name="Table" size={64} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No Data Available</h3>
          <p className="text-text-muted">Select a report to view detailed data table</p>
        </div>
      </div>
    );
  }

  const columns = getTableColumns();

  return (
    <div className="bg-surface border border-border rounded-lg">
      {/* Table Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-text-primary">Summary Data</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-text-muted">Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 bg-background border border-border rounded text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-text-muted">entries</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-background border-b border-border">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-left text-sm font-medium text-text-primary ${
                    column.sortable ? 'cursor-pointer hover:bg-hover' : ''
                  }`}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {column.sortable && (
                      <Icon
                        name={
                          sortField === column.key
                            ? sortDirection === 'asc' ?'ChevronUp' :'ChevronDown' :'ChevronsUpDown'
                        }
                        size={14}
                        className="text-text-muted"
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr
                key={item.id}
                className={`border-b border-border hover:bg-hover transition-colors duration-150 ${
                  index % 2 === 0 ? 'bg-background/50' : ''
                }`}
              >
                {columns.map((column) => (
                  <td key={column.key} className="px-4 py-3 text-sm text-text-primary">
                    {renderCellContent(item, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-muted">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} entries
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              iconName="ChevronLeft"
              className="p-2"
            />
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "primary" : "ghost"}
                    onClick={() => setCurrentPage(pageNum)}
                    className="px-3 py-1 text-sm"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              {totalPages > 5 && (
                <>
                  <span className="text-text-muted">...</span>
                  <Button
                    variant={currentPage === totalPages ? "primary" : "ghost"}
                    onClick={() => setCurrentPage(totalPages)}
                    className="px-3 py-1 text-sm"
                  >
                    {totalPages}
                  </Button>
                </>
              )}
            </div>
            
            <Button
              variant="ghost"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              iconName="ChevronRight"
              className="p-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportSummaryTable;